/** /notifications/themes — cross-tenant Notification_Theme catalog (grid +
 *  create/edit dialog). A theme styles deliveries: an email shell wrapped
 *  around the rendered body, design tokens exposed as `theme.*`, optional CSS
 *  inlining, and per-channel text signatures; a null tenant is a system-wide
 *  theme. Filters (operator scope / enabled) and search are held in local
 *  state and passed to the list query as `extra`. */
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import * as themesApi from '@/api/notificationThemes';
import { type NotificationThemeListItem } from '@/api/notificationThemes';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { batchEditAction } from '@/features/batch-edit/batchEditAction';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import {
  pickedSentinel,
  TenantPicker,
  type TenantPickerValue,
} from '@/components/TenantPicker/TenantPicker';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { useEditIntent } from '@/hooks/useEditIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds } from '@/scope/tenantIds';
import { useScopedTenantsState } from '@/scope/useScopedTenantsState';

import { ThemeFormDialog } from './ThemeFormDialog';

/** Sentinel picker option: system-wide themes (`tour_operator IS NULL`). */
const SYSTEM_TENANT: TenantPickerValue = { id: 0, name: 'System / global (no operator)' };
const SYSTEM_EXTRA_OPTIONS = [SYSTEM_TENANT];

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString() : '—');

export function ThemesListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_notification_themes_manage');

  // Dialog: null theme + open → create; a fetched detail → edit.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // Filters (local state → list `extra`). The operators (several, D1) or the
  // System sentinel alone (D3); they follow the console scope until picked here.
  const [tenants, setTenants] = useScopedTenantsState();
  const [enabledFilter, setEnabledFilter] = useState<'any' | 'true' | 'false'>('any');
  const tenantIds = formatTenantIds(tenants.map((t) => t.id));
  const tenantParam = pickedSentinel(tenants, SYSTEM_EXTRA_OPTIONS) ? 'system' : tenantIds;

  const anyFilterActive = tenants.length > 0 || enabledFilter !== 'any';
  const resetAllFilters = () => {
    setTenants([]);
    setEnabledFilter('any');
  };

  const extra = useMemo(() => {
    const e: Record<string, string | number | boolean> = {};
    if (tenantParam) e.tour_operator_id = tenantParam;
    if (enabledFilter !== 'any') e.enabled = enabledFilter === 'true';
    return e;
  }, [tenantParam, enabledFilter]);

  // The edit dialog needs the full Detail; fetch it on demand.
  const editQuery = useQuery({
    queryKey: ['notification_themes', 'detail', editId],
    queryFn: () => themesApi.retrieve(editId as number),
    enabled: editId !== null,
  });

  // The create dialog features the page's operators and pre-selects the one when
  // there is exactly one (D9); the System sentinel (id 0) names no operator.
  const pageTenants = pageTenantsFrom(tenantIds, tenants);

  useCreateIntent(() => openCreate());

  const openCreate = () => {
    setEditId(null);
    setDialogOpen(true);
  };
  const openEdit = useCallback((id: number) => {
    setEditId(id);
    setDialogOpen(true);
  }, []);
  useEditIntent(openEdit);

  const columns = useMemo<GridColDef<NotificationThemeListItem>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'codename',
        headerName: 'Codename',
        flex: 1,
        minWidth: 200,
        renderCell: (p) =>
          canManage ? (
            <Box
              component="button"
              type="button"
              onClick={() => openEdit(p.row.id)}
              sx={{
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                color: '#0a1a3f',
                fontWeight: 500,
                font: 'inherit',
                textAlign: 'left',
              }}
            >
              {p.row.codename}
            </Box>
          ) : (
            p.row.codename
          ),
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        valueGetter: (_, row) => row.name ?? '',
      },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        valueGetter: (_, row) => row.tenant?.name ?? 'System',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} empty="System" />,
      },
      {
        field: 'enabled',
        headerName: 'Enabled',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) =>
          p.value ? <CheckIcon fontSize="small" color="success" aria-label="yes" /> : null,
      },
      {
        field: 'inline_css',
        headerName: 'Inline CSS',
        width: 110,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: (p) =>
          p.value ? <CheckIcon fontSize="small" color="success" aria-label="yes" /> : null,
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 130,
        valueGetter: (_, row) => row.updated_ts,
        renderCell: (p) => fmtDate(p.row.updated_ts),
      },
    ],
    [canManage, openEdit],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      themesApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'codename',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'scope',
      priority: 5,
      active: tenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={tenants}
            onChange={setTenants}
            label="Operator"
            extraOptions={SYSTEM_EXTRA_OPTIONS}
          />
        </Box>
      ),
    },
    {
      key: 'enabled',
      priority: 3,
      active: enabledFilter !== 'any',
      node: (
        <TextField
          select
          size="small"
          label="Enabled"
          value={enabledFilter}
          sx={{ width: 160 }}
          onChange={(e) => setEnabledFilter(e.target.value as 'any' | 'true' | 'false')}
        >
          <MenuItem value="any">Any</MenuItem>
          <MenuItem value="true">Enabled</MenuItem>
          <MenuItem value="false">Disabled</MenuItem>
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="notification-themes"
        title="Themes"
        subtitle="Notification themes — email shell + design tokens + text-channel signatures applied at delivery. A system theme has no owning operator."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New theme
            </Button>
          )
        }
      />

      <ResponsiveFilterBar items={toolbarItems} />

      <ResourceDataGrid<NotificationThemeListItem>
        queryKey={['notification_themes']}
        queryFn={queryFn}
        columns={columns}
        extra={extra}
        batchResource="notification_themes"
        batchActions={[
          deleteBatchAction('console_notification_themes_manage'),
          batchEditAction('notification_themes', 'theme'),
        ]}
        defaultSort={{ field: 'codename', sort: 'asc' }}
        emptyMessage="No themes yet."
        headerToolbar={{
          onResetAllFilters: resetAllFilters,
          resetDisabled: !anyFilterActive,
          search: true,
          searchPlaceholder: 'Codename, name…',
          exportFileName: 'notification-themes',
        }}
      />

      {/* Create: theme=null immediately. Edit: wait for the detail fetch. */}
      {dialogOpen && (editId === null || editQuery.data) && (
        <ThemeFormDialog
          open={dialogOpen}
          theme={editId === null ? null : (editQuery.data ?? null)}
          pageTenants={pageTenants}
          onClose={() => {
            setDialogOpen(false);
            setEditId(null);
          }}
        />
      )}
    </Box>
  );
}
